import { useQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { MultipleTenantPicker, TenantOption } from "components/MultipleTenantPicker";
import { StaffFacilityPickerQuery } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useCallback, useMemo, useState } from "react";
import { STAFF_FACILITY_PICKER_QUERY } from "routes/Staff/Credentials/CredentialManagement/api";

type Row = StaffFacilityPickerQuery["facilities"]["items"][number];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// A factory, not a shared constant: the model holds a live `Set`, so one instance
// handed out as the reset value would carry mutations into every later reset.
const emptySelection = (): GridRowSelectionModel => ({ type: "include", ids: new Set() });

function buildColumns(linkedFacilityIds: Set<string>) {
  return [
    {
      field: "facility",
      headerName: "Facility",
      flex: 1.4,
      sortable: false,
      valueGetter: (_: unknown, row: Row) =>
        row.customerFacilityId ? `${row.name} (${row.customerFacilityId})` : row.name,
    },
    {
      field: "tenant",
      headerName: "Organization",
      flex: 1,
      sortable: false,
      valueGetter: (_: unknown, row: Row) => row.tenant?.name ?? "—",
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      sortable: false,
      valueGetter: (_: unknown, row: Row) =>
        [row.streetAddress1, row.city, row.state, row.zip].filter(Boolean).join(", "),
    },
    {
      field: "linked",
      headerName: "Already linked",
      flex: 0.5,
      align: "center" as const,
      headerAlign: "center" as const,
      sortable: false,
      renderCell: ({ row }: { row: Row }) =>
        linkedFacilityIds.has(row.id) ? <Check fontSize="small" /> : null,
    },
  ];
}

/**
 * Selection spans pages, and a row leaves `items` when its page does — so each row is
 * captured when it's picked rather than looked up at save time.
 */
function useCrossPageSelection(current: StaffFacilityPickerQuery | undefined) {
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [selectedRows, setSelectedRows] = useState<Map<string, Row>>(new Map());

  const selectedIds = useMemo(() => Array.from(selection.ids, (id) => id.toString()), [selection]);

  // Under server-side pagination an `exclude` model means every facility in the
  // database, not every visible row — dropped rather than read as a huge selection.
  const onSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const next = model.type === "exclude" ? emptySelection() : model;
      setSelection(next);
      setSelectedRows((previous) => {
        const rows = new Map<string, Row>();
        for (const rowId of next.ids) {
          const id = rowId.toString();
          const row = previous.get(id) ?? current?.facilities.items.find((item) => item.id === id);
          if (row) rows.set(id, row);
        }
        return rows;
      });
    },
    [current]
  );

  return { selection, selectedIds, selectedRows, onSelectionChange };
}

function PickerFilters({
  search,
  onSearchChange,
  tenants,
  onTenantChange,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  tenants: TenantOption[];
  onTenantChange: (value: TenantOption[] | null) => void;
}) {
  return (
    // Both wrapped: MultipleTenantPicker renders full-width internally and ignores an
    // `sx` flex, which collapses a sibling flex item to zero.
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          label="Search facilities"
          size="small"
          fullWidth
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <MultipleTenantPicker
          label="Organization"
          value={tenants}
          onChange={onTenantChange}
          lazyLoad={false}
          textFieldProps={{ size: "small" }}
          invalid={false}
          isTouched={false}
          isDirty={false}
          isValidating={false}
          hideErrors
        />
      </Box>
    </Stack>
  );
}

type StaffFacilityPickerProps = {
  open: boolean;
  /** Drives the "Linked" column; re-linking is a server-side no-op. */
  linkedFacilityIds: Set<string>;
  saving?: boolean;
  /**
   * Rows, not ids: the caller holds these as pending state and renders them grouped by
   * tenant before the save, so it needs the name and tenant of each one.
   */
  onSave: (facilities: Row[]) => void;
  onCancel: () => void;
};

/**
 * Cross-tenant facility picker.
 *
 * Deliberately not `OmnisearchDataGridV2`: its store is a module-level singleton over
 * `window.location.search`, so a V2 grid here would share `?omnisearch=` with the
 * credentials grid behind it. `useLocalOmnisearch` exists for modals but neither the
 * V2 grid nor its toolbar can consume it, so this isn't an omnisearch grid at all.
 *
 * Adds only — unlinking lives on the credential, which avoids round-tripping
 * thousands of ids to express "keep these". Mounted only while open by its caller,
 * so all its state resets between opens.
 */
export function StaffFacilityPicker({
  open,
  linkedFacilityIds,
  saving,
  onSave,
  onCancel,
}: StaffFacilityPickerProps) {
  const [search, setSearch] = useState("");
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const [debouncedSearch] = useDebounce(search, 500);

  // `tenantId:a|b` is the same wire format MultipleTenantPicker produces for the
  // staff grids; the facilities omnisearch resolves it to `tenantId: { in: [...] }`.
  const omnisearch = useMemo(() => {
    const terms = [debouncedSearch.trim()];
    if (tenants.length) {
      terms.push(`tenantId:${tenants.map((tenant) => tenant.id).join("|")}`);
    }
    return terms.filter(Boolean).join(" ");
  }, [debouncedSearch, tenants]);

  const { data, previousData, loading } = useQuery(STAFF_FACILITY_PICKER_QUERY, {
    variables: {
      search: omnisearch,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      // `id` breaks ties: facility names aren't unique, and without a tiebreaker rows
      // repeat or vanish across pages — so a selection can refer to an unseen row.
      sort: [
        { field: "facility", sort: "asc" },
        { field: "id", sort: "asc" },
      ],
    },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const current = data ?? previousData;

  const { selection, selectedIds, selectedRows, onSelectionChange } =
    useCrossPageSelection(current);

  const columns = useMemo(() => buildColumns(linkedFacilityIds), [linkedFacilityIds]);

  // Already-linked rows aren't selectable, so the caller's "N linked" is the number
  // of links actually created rather than a count including no-ops.
  const isRowSelectable = useCallback(
    (params: { id: GridRowId }) => !linkedFacilityIds.has(params.id.toString()),
    [linkedFacilityIds]
  );

  const handleSave = useCallback(() => {
    onSave(selectedIds.flatMap((id) => selectedRows.get(id) ?? []));
  }, [onSave, selectedIds, selectedRows]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleTenantChange = useCallback((value: TenantOption[] | null) => {
    setTenants(value ?? []);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      fullWidth
      title="Add facilities"
      description="Facilities from any organization can be linked to this credential."
      primaryActionButtonProps={{
        saveText: "Add facilities",
        loading: saving,
        disabled: selectedIds.length === 0 || saving,
        onClick: handleSave,
      }}
    >
      <Stack spacing={2}>
        <PickerFilters
          search={search}
          onSearchChange={handleSearchChange}
          tenants={tenants}
          onTenantChange={handleTenantChange}
        />
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length === 0
            ? "Select one or more facilities."
            : `${selectedIds.length} selected`}
        </Typography>
        <DataGrid<Row>
          loading={loading}
          rows={current?.facilities.items ?? []}
          rowCount={current?.facilities.count ?? 0}
          columns={columns}
          checkboxSelection
          checkboxSelectionVisibleOnly
          keepNonExistentRowsSelected
          isRowSelectable={isRowSelectable}
          rowSelectionModel={selection}
          onRowSelectionModelChange={onSelectionChange}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          sx={{ minHeight: 360 }}
        />
      </Stack>
    </Dialog>
  );
}
